import React, { useEffect, useMemo, useRef, useState } from "react";

type Props = {
    /** Totale duur van 1 loop (ms) */
    durationMs?: number;

    /** Pauzeer */
    paused?: boolean;

    /** Render size (px). Laat leeg voor responsive */
    width?: number;
    height?: number;

    /** Accent/goud */
    goldColor?: string;

    /** Glow sterkte */
    glowBlur?: number;

    /** Hoe “sterk” de sweep zichtbaar is (0..1) */
    sweepStrength?: number;

    /** Basiskleur van de lijnen */
    baseColor?: string;
};

function clamp(n: number, min: number, max: number) {
    return Math.max(min, Math.min(max, n));
}
function clamp01(n: number) {
    return clamp(n, 0, 1);
}
function lerp(a: number, b: number, t: number) {
    return a + (b - a) * t;
}
function easeInOut(t: number) {
    return t * t * (3 - 2 * t);
}

function usePrefersReducedMotion() {
    const [reduced, setReduced] = useState(false);
    useEffect(() => {
        const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
        if (!mq) return;
        const onChange = () => setReduced(!!mq.matches);
        onChange();
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return reduced;
}

type Ping = {
    id: string;
    r: number; // 0..1
    a: number; // degrees
    t0: number; // 0..1
};

/**
 * PreventScene — Radar Sweep (fixed baseColor)
 * - Geen resolvedTheme / prefers-color-scheme
 * - 1 vaste lijnkleur: baseColor (#8A8A8A)
 */
export default function PreventScene({
    durationMs = 9000,
    paused = false,
    width,
    height,
    goldColor = "rgb(var(--primary))",
    glowBlur = 0.85,
    sweepStrength = 0.2,
    baseColor = "#8A8A8A",
}: Props) {
    const reduced = usePrefersReducedMotion();

    // Vaste “sterkte”
    const ringOpacity = 1.0;
    const gridOpacity = 0.92;
    const spokeOpacity = 0.92;

    // Strakker op wit
    const ringWidth = 1.0;
    const gridWidth = 0.75;
    const spokeWidth = 0.75;

    const ids = useMemo(() => {
        const s = Math.random().toString(16).slice(2);
        return {
            glow: `rd-glow-${s}`,
            cone: `rd-cone-${s}`,
        };
    }, []);

    const rafRef = useRef<number | null>(null);
    const startRef = useRef<number | null>(null);
    const [tMs, setTMs] = useState(0);

    const safeDuration = Number.isFinite(durationMs) && durationMs > 0 ? durationMs : 9000;

    useEffect(() => {
        if (reduced) {
            setTMs(safeDuration);
            return;
        }

        const tick = (now: number) => {
            if (startRef.current === null) startRef.current = now;

            if (!paused) {
                const elapsed = now - startRef.current;
                setTMs(elapsed % safeDuration);
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            startRef.current = null;
        };
    }, [paused, safeDuration, reduced]);

    const p = reduced ? 1 : tMs / safeDuration;
    const angle = -90 + 360 * p;

    const pings: Ping[] = useMemo(
        () => [
            { id: "p1", r: 0.52, a: 35, t0: 0.18 },
            { id: "p2", r: 0.72, a: 150, t0: 0.46 },
            { id: "p3", r: 0.40, a: 265, t0: 0.72 },
        ],
        []
    );

    const polar = (cx: number, cy: number, R: number, deg: number, rr: number) => {
        const rad = (deg * Math.PI) / 180;
        return { x: cx + Math.cos(rad) * R * rr, y: cy + Math.sin(rad) * R * rr };
    };

    const pingAlpha = (t0: number) => {
        const dt = (p - t0 + 1) % 1;
        const inWin = clamp01(1 - dt / 0.18);
        const up = easeInOut(clamp01(dt / 0.03));
        const down = clamp01(1 - (dt - 0.03) / 0.15);
        return inWin * up * down;
    };

    const coneOpacity = 0.10 + 0.12 * sweepStrength;

    const svgSizing =
        typeof width === "number" && typeof height === "number"
            ? { width, height }
            : { width: "100%", height: "100%" as const };

    const VB = 100;
    const cx = 50;
    const cy = 50;
    const R = 36;

    return (
        <svg
            viewBox={`0 0 ${VB} ${VB}`}
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid meet"
            style={{ display: "block" }}
            role="img"
            aria-label="Preventie: Radar sweep die afwijkingen vroeg signaleert"
            {...svgSizing}
        >
            <defs>
                <filter id={ids.glow} x="-160%" y="-160%" width="420%" height="420%">
                    <feGaussianBlur stdDeviation={glowBlur} result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>

                <radialGradient id={ids.cone} cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={goldColor} stopOpacity="0.0" />
                    <stop offset="55%" stopColor={goldColor} stopOpacity="0.12" />
                    <stop offset="100%" stopColor={goldColor} stopOpacity="0.0" />
                </radialGradient>
            </defs>

            {/* Ringen + grid + spokes */}
            <g
                stroke={baseColor}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                shapeRendering="geometricPrecision"
            >
                <circle cx={cx} cy={cy} r={R} strokeWidth={ringWidth} opacity={ringOpacity} />

                <circle
                    cx={cx}
                    cy={cy}
                    r={R * 0.75}
                    strokeWidth={gridWidth}
                    opacity={gridOpacity}
                    strokeDasharray="2.5 3.5"
                />
                <circle
                    cx={cx}
                    cy={cy}
                    r={R * 0.5}
                    strokeWidth={gridWidth}
                    opacity={gridOpacity}
                    strokeDasharray="2.5 3.5"
                />
                <circle
                    cx={cx}
                    cy={cy}
                    r={R * 0.25}
                    strokeWidth={gridWidth}
                    opacity={gridOpacity}
                    strokeDasharray="2.5 3.5"
                />

                <path d={`M ${cx - R} ${cy} H ${cx + R}`} strokeWidth={spokeWidth} opacity={spokeOpacity} />
                <path d={`M ${cx} ${cy - R} V ${cx} ${cy + R}`} strokeWidth={spokeWidth} opacity={spokeOpacity} />
                <path
                    d={`M ${cx - R * 0.707} ${cy - R * 0.707} L ${cx + R * 0.707} ${cy + R * 0.707}`}
                    strokeWidth={spokeWidth}
                    opacity={spokeOpacity}
                />
                <path
                    d={`M ${cx + R * 0.707} ${cy - R * 0.707} L ${cx - R * 0.707} ${cy + R * 0.707}`}
                    strokeWidth={spokeWidth}
                    opacity={spokeOpacity}
                />
            </g>

            {/* Sweep cone */}
            {!reduced && (
                <g transform={`rotate(${angle} ${cx} ${cy})`} opacity={coneOpacity}>
                    <path
                        d={[
                            `M ${cx} ${cy}`,
                            `L ${cx + R} ${cy}`,
                            `A ${R} ${R} 0 0 1 ${cx + R * Math.cos((32 * Math.PI) / 180)} ${cy + R * Math.sin((32 * Math.PI) / 180)}`,
                            "Z",
                        ].join(" ")}
                        fill={`url(#${ids.cone})`}
                    />
                </g>
            )}

            {/* Sweep line */}
            <g
                transform={`rotate(${angle} ${cx} ${cy})`}
                opacity={0.22 + 0.18 * sweepStrength}
                filter={`url(#${ids.glow})`}
            >
                <path d={`M ${cx} ${cy} L ${cx + R} ${cy}`} stroke={goldColor} strokeWidth="1.05" strokeLinecap="round" />
            </g>

            {/* Pings */}
            {pings.map((pg) => {
                const a = pingAlpha(pg.t0);
                const pos = polar(cx, cy, R, pg.a, pg.r);
                const ringR = lerp(1.6, 4.8, easeInOut(clamp01(a * 1.2)));

                return (
                    <g key={pg.id} opacity={a}>
                        <circle cx={pos.x} cy={pos.y} r={1.35} fill={goldColor} filter={`url(#${ids.glow})`} />
                        <circle cx={pos.x} cy={pos.y} r={ringR} stroke={goldColor} strokeWidth={1.45} fill="none" filter={`url(#${ids.glow})`} />
                    </g>
                );
            })}
        </svg>
    );
}